/**
 * The base of LSEG's AdFin request processors (RDP.Date*, RDP.Fx*), ported
 * from Workspace's Excel add-in (Refinitiv for Office 26.30.2): arguments by
 * name (AFArgIndex), dates, periods, tenors, calendar and currency codes,
 * output fields, and the same read from cell ranges (with the FX swap points).
 *
 * The method names are LSEG's, so the logic can be compared with the source;
 * each carries a `// LSEG: <module>.<method>` pointer. Behaviour the source
 * does not settle is marked [A].
 */
import { ADDITIONAL_TENOR_TYPES, FX_SOURCES } from "./data.generated";
import { AdFinUtils, AdFinValidationError, CellRange, isFxSource, isNumber, withoutEmptyValues } from "./utils";

/** LSEG's argument names (AFArgIndex). */
// LSEG: AdFinType_70754.AFArgIndex
export const AF_ARG = {
  adFinMain: "adfinmain",
  adFinType: "adfintype",
  fields: "fields",
  options: "options",
  destinationCell: "destinationcell",
  calculationDate: "calculationdate",
  periods: "periods",
  calendarCodes: "calendarcodes",
  currencyCodes: "currencycodes",
  dateMovingConvention: "datemovingconvention",
  startDate: "startdate",
  start: "start",
  endDate: "enddate",
  end: "end",
  periodType: "periodtype",
  dayCountBasis: "daycountbasis",
  dayNumber: "daynumber",
  day: "day",
  direction: "direction",
  referenceDate: "referencedate",
  fxCrossCode: "fxcrosscode",
  marketDataDate: "marketdatadate",
  tenors: "tenors",
  spotCcy1: "spotccy1",
  spotCcy2: "spotccy2",
  swapPointsCcy1: "swappointsccy1",
  swapPointsCcy2: "swappointsccy2",
  depRatesCcy1: "depratesccy1",
  depRatesCcy2: "depratesccy2",
  zcCurve: "zccurve",
  xValue: "xvalue",
  xValues: "xvalues",
  yValues: "yvalues",
  interpMethod: "interpmethod",
} as const;
export type AFArgName = (typeof AF_ARG)[keyof typeof AF_ARG];

/** `{ calculationDate: "Calculation Date" }`: an argument's label key and its name in messages. */
export type ArgName = Record<string, string>;

const LIST_SEPARATORS = /[,;|]/;

/** Splits LSEG's `{ key: "Name" }` argument descriptor into [label, name]. */
function labelAndName(argName: ArgName): [string, string] {
  const key = `${Object.keys(argName)[0]}`;
  return [key.toLowerCase(), argName[key]];
}

type CodeType = "calendar" | "currency";

/** [A] null, undefined or blank text (see processCellRangeCalendarCurrencyCodes). */
function isBlankCell(cell: unknown): boolean {
  return cell === null || cell === undefined || (typeof cell === "string" && cell.trim() === "");
}

// LSEG: ECellRangeOptions_7036.ECellRangeOptions
export type ECellRangeOption = "rh" | "ch";

const SPECIAL_SYMBOLS = /[ :;,()\[\]]/;
const SPECIAL_SYMBOLS_GLOBAL = new RegExp(SPECIAL_SYMBOLS, "g");

/**
 * A cell range as one text, the way LSEG passes a range on: one cell or one
 * column is a ";" list, and a wider range is "(key:value ...);(...)" groups
 * keyed by its first column. Only the "rh" (row header) reading is ported:
 * it is the one the AdFin functions use.
 */
// LSEG: CellRangeProcessor_76928.CellRangeProcessor
export class CellRangeProcessor {
  // LSEG: CellRangeProcessor_76928.processCellRange
  processCellRange(range: unknown, option: "rh"): string {
    if (!Array.isArray(range)) return `${range === null || range === undefined || range === "" ? "''" : range}`;
    const cells = range as CellRange;
    if (option === "rh" && (cells[0]?.length ?? 0) <= 1) return this.processCellRangeArray(cells);
    return `(${this.rowObjectBuilder(cells)})`; // LSEG: CellRangeProcessor_76928.processCellRangeObject
  }

  // LSEG: CellRangeProcessor_76928.processCellRangeArray
  private processCellRangeArray(range: CellRange): string {
    const values =
      range.length === 1
        ? Array.isArray(range[0])
          ? range[0].map((cell) => this.getColumnValue(cell)[0])
          : [`${this.getColumnValue(range[0])}`]
        : range.map((row) => this.getColumnValue(row[0])[0]);
    return values.length === 0 || (values.length === 1 && values[0] === "") ? "''" : values.join(";");
  }

  /** Per value column, "key:value" pairs joined by spaces. */
  // LSEG: CellRangeProcessor_76928.getRowObject
  private getRowObject(range: CellRange): string[] {
    const out: string[] = [];
    range.forEach((row) => {
      const key = this.getKeyValue(row[0]);
      if (!key) return;
      row.slice(1).forEach((cell, i) => {
        const [value, empty] = this.getColumnValue(cell);
        if (empty) return;
        out[i] = `${out[i] ? `${out[i]} ` : ""}${key}:${value}`;
      });
    });
    return out;
  }

  // LSEG: CellRangeProcessor_76928.rowObjectBuilder
  private rowObjectBuilder(range: CellRange): string {
    if ((range?.length ?? 0) === 0 || (range[0]?.length ?? 0) < 2) return "";
    let out = "";
    this.getRowObject(range).forEach((group) => {
      if (group) out += `${out ? ");(" : ""}${group}`;
    });
    return out;
  }

  /** A key without spaces, colons, separators or brackets. */
  // LSEG: CellRangeProcessor_76928.getKeyValue
  private getKeyValue(cell: unknown): string {
    return cell ? `${cell}`.replace(SPECIAL_SYMBOLS_GLOBAL, "") : "";
  }

  /** [the value, quoted when it holds a special symbol; whether the cell is empty]. */
  // LSEG: CellRangeProcessor_76928.getColumnValue
  private getColumnValue(cell: unknown): [string, boolean] {
    if (cell === null || cell === undefined || cell === "") return ["", true];
    const text = `${cell}`.trim();
    return SPECIAL_SYMBOLS.test(text) ? [`'${text}'`, false] : [text, false];
  }
}

const SINGLE_ARGUMENT_LABELS = ["fxcrosscode", "marketdatadate"];

/** A swap-point row (or column) being read. */
type SwapPointsRow = { rowTenor: unknown; rowBid: unknown; rowAsk: unknown };
/** Swap points read so far: what LSEG sends as `swapPointsCcy*` / `depRatesCcy*`. */
export type ForexOverrides = {
  additionalTenorTypes?: (string | undefined)[];
  source?: string;
  overrides?: Record<string, unknown>[];
};

/** The same inputs read from a cell range. */
// LSEG: AFCellRangeProcessor_29882.AFCellRangeProcessor
export class AFCellRangeProcessor {
  private readonly adFinUtils = new AdFinUtils();
  private readonly cellRangeProcessor = new CellRangeProcessor();
  private readonly codeTypesMap: Record<
    CodeType,
    { fieldName: string; label: string; processCodes: (codes: string) => string[] }
  > = {
    calendar: {
      fieldName: "Calendar Codes",
      label: "calendarcodes",
      processCodes: (codes) => this.adFinUtils.processCalendarCodes(codes),
    },
    currency: {
      fieldName: "Currency Codes",
      label: "currencycodes",
      processCodes: (codes) => this.adFinUtils.processCurrencyCodes(codes),
    },
  };

  /** [is a date or a tenor, the ISO date or the tenor text]. */
  // LSEG: AFCellRangeProcessor_29882.getTenor
  getTenor(value: unknown): [boolean, string] {
    if (!value) return [false, ""];
    const text = `${value}`.trim().replace(/\s{2,}/g, " ");
    try {
      if (typeof value === "number") return [true, this.adFinUtils.getFormattedDate({ date: Math.floor(value) })!];
      return [true, this.adFinUtils.getFormattedDate({ date: text, fieldName: "Tenor" })!];
    } catch {
      // not a date: a tenor, maybe
    }
    return [this.adFinUtils.isTenor(text), text];
  }

  /** A period from one cell, or a label cell then the period. */
  // LSEG: AFCellRangeProcessor_29882.processCellRangePeriod
  processCellRangePeriod(range: CellRange, argName: ArgName): string | undefined {
    const [label, name] = labelAndName(argName);
    if (!range?.length) return undefined;
    if (range[0].length > 1 && range.length > 1) this.adFinUtils.validate(false, name, "not in the right format");
    const second = range[0].length > 1 ? range[0][1] : range.length > 1 ? range[1][0] : range[0][0];
    const labelled = this.adFinUtils.compareLabel(range[0][0], label);
    if (!labelled && (range[0].length > 1 || range.length > 1)) {
      this.adFinUtils.notValid(name, "wrong label or not in the right format");
    }
    const [valid, period] = this.getTenor(labelled ? second : range[0][0]);
    this.adFinUtils.validate(valid, name, "not in the right format");
    return period;
  }

  /** A date from one cell, or a label cell then the date. */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeAdfinDate
  processCellRangeAdfinDate(range: CellRange, argName: ArgName): string | null {
    const [label, name] = labelAndName(argName);
    if ((range[0].length > 1 && range.length > 1) || range[0].length === 0) {
      this.adFinUtils.validate(false, name, "not in the right format");
    }
    const second = range[0].length > 1 ? range[0][1] : range.length > 1 ? range[1][0] : range[0][0];
    const labelled = this.adFinUtils.compareLabel(range[0][0], label);
    if (!labelled && (range[0].length > 1 || range.length > 1)) {
      this.adFinUtils.notValid(name, "wrong label or not in the right format");
    }
    const date = labelled ? second : range[0][0];
    if (typeof date === "number") return this.adFinUtils.getFormattedDate({ date: Math.floor(date) });
    if (typeof date !== "string") this.adFinUtils.notValid(name, "not in the right format");
    return this.adFinUtils.getFormattedDate({ date, fieldName: name });
  }

  /**
   * Codes from one row or one column, a leading "Calendar Codes" /
   * "Currency Codes" label cell skipped; a cell may hold several codes.
   * [A] A number cell in a 1x1 range or a column is read as its text; LSEG
   * would fail on it there (its row branch already reads the text).
   * [A] Blank cells (null, undefined, blank text) are skipped in every shape;
   * the getters treat an all-blank range as an empty argument. LSEG differs by
   * shape: a null in a row becomes the calendar "NULL", and a 1x1 or a column
   * throws a TypeError. Not captured; a capture would settle it.
   */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeCalendarCurrencyCodes
  processCellRangeCalendarCurrencyCodes(range: CellRange, codeType: CodeType): string[] {
    const type = this.codeTypesMap[codeType];
    if (range[0].length > 1 && range.length > 1) this.adFinUtils.validate(false, type.fieldName, "not in the right format");
    if (range[0].length === 1 && range.length === 1) {
      return isBlankCell(range[0][0]) ? [] : type.processCodes(`${range[0][0]}`);
    }
    const out: string[] = [];
    if (range[0].length > 1) {
      range[0]
        .filter((cell) => !isBlankCell(cell) && this.adFinUtils.fieldNameToLabel(cell) !== type.label)
        .forEach((cell) => out.push(...type.processCodes(`${cell}`)));
    } else if (range.length > 1) {
      range
        .filter((row) => !isBlankCell(row[0]) && this.adFinUtils.fieldNameToLabel(row[0]) !== type.label)
        .forEach((row) => out.push(...type.processCodes(`${row[0]}`)));
    }
    return out;
  }

  /** [A] Every cell is blank (see processCellRangeCalendarCurrencyCodes). */
  isBlankRange(range: CellRange): boolean {
    return range.every((row) => row.every(isBlankCell));
  }

  /** Names from one row or one column (a leading label cell skipped), mapped through `allowed`. */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeArgument
  processCellRangeArgument(range: CellRange, allowed: Record<string, string>, fieldName: string): string[] {
    const label = this.adFinUtils.fieldNameToLabel(fieldName);
    this.adFinUtils.validate(range?.length !== 0 && range?.[0].length !== 0, fieldName, "not in the right format");
    this.adFinUtils.validate(range[0].length === 1 || range.length === 1, fieldName, "not in the right format");
    const labelled = this.adFinUtils.compareLabel(range[0][0], label);
    const names: string[] = [];
    if (range[0].length > 1) {
      range[0].forEach((cell, i) => {
        if (labelled && i === 0) return;
        names.push(...this.adFinUtils.fieldNameToLabel(cell).split(LIST_SEPARATORS));
      });
    } else if (range.length >= 1) {
      range.forEach((row, i) => {
        if (labelled && i === 0) return;
        names.push(...this.adFinUtils.fieldNameToLabel(row[0]).split(LIST_SEPARATORS));
      });
    }
    const out: string[] = [];
    for (const n of names) {
      const value = allowed[n];
      this.adFinUtils.validate(value !== undefined, fieldName, "not in correct format");
      out.push(value);
    }
    this.adFinUtils.validate(out.length > 0, fieldName, "not in the correct format");
    return out;
  }

  /** One value from one cell, or a label cell then the value, passed through `read`. */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeSingleValueParam
  processCellRangeSingleValueParam<T>(range: CellRange, fieldName: string, read: (value: unknown) => T): T {
    let label: unknown;
    let value: unknown;
    if (range[0].length > 1 && range.length > 1) this.adFinUtils.validate(false, fieldName, "not in the right format");
    if (range.length === 1 && range[0].length === 1) {
      value = range[0][0];
    } else if (range.length > 1) {
      label = range[0][0];
      value = range[1][0];
    } else {
      label = range[0][0];
      value = range[0][1];
    }
    if (label) this.adFinUtils.validateLabel(label, fieldName);
    return read(value);
  }

  // ---------------------------------------------------------------- FX

  /**
   * The text of one cell, or of the label cell "fxCrossCode" (index 0) or
   * "marketDataDate" (index 1) then the value cell.
   */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeSingleArgument
  processCellRangeSingleArgument(range: CellRange, index: number): string {
    const label = SINGLE_ARGUMENT_LABELS[index];
    const check = (typed: string | undefined) => {
      // LSEG throws a plain Error with this text, as its notValid does; it is shown the same way.
      if (typed !== label) throw new AdFinValidationError(`Argument ${label} is not in the right format!`);
    };
    const text = this.cellRangeProcessor.processCellRange(range, "rh");
    if (text.includes(";")) {
      check(text.split(";")[0]?.toLowerCase());
      return text.split(";")[1];
    }
    if (text.includes(":")) {
      const pair = text.slice(1, -1);
      check(pair.split(":")[0]?.toLowerCase());
      return pair.split(":")[1];
    }
    return text;
  }

  /** Tenors (or dates) from one row or one column, a leading "Tenors" label skipped; a cell may hold several. */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeTenors
  processCellRangeTenors(range: CellRange): string[] {
    const fieldName = "Tenors";
    let tenors: string[] = [];
    let problems: string[] = [];
    let valid = true;
    if (range[0].length > 1 && range.length > 1) this.adFinUtils.validate(false, fieldName, "not in the right format");
    if (range[0].length > 1) {
      [tenors, problems, valid] = this.getCellRangeTenors(range[0], valid, fieldName.toLowerCase());
    } else if (range.length >= 1) {
      [tenors, problems, valid] = this.getCellRangeTenors(
        range.map((row) => row[0]),
        valid,
        fieldName.toLowerCase(),
      );
    }
    this.adFinUtils.validate(valid, fieldName, problems.join(", "));
    return tenors;
  }

  /** [tenors, "'x' is not valid tenor" texts, all valid]: a number is an Excel date; a text is split at , ; |. */
  // LSEG: AFCellRangeProcessor_29882.getCellRangeTenorsInRow / getCellRangeTenorsInColumn (the same reading of a row's or a column's cells)
  private getCellRangeTenors(cells: unknown[], valid: boolean, label: string): [string[], string[], boolean] {
    const tenors: string[] = [];
    const problems: string[] = [];
    cells.forEach((cell, i) => {
      if (typeof cell === "number") {
        tenors.push(this.getTenor(cell)[1]);
        return;
      }
      const text = `${cell}`.trim().replace(/\s{2,}/g, " ");
      if (i === 0 && text.toLowerCase() === label) return;
      text.split(LIST_SEPARATORS).forEach((part) => {
        const [ok, tenor] = this.getTenor(part);
        if (ok) {
          tenors.push(tenor);
        } else {
          valid = false;
          problems.push(`'${part}' is not valid tenor`);
        }
      });
    });
    return [tenors, problems, valid];
  }

  /**
   * Swap points (or deposit rates) from a range: a tenor, a bid and an ask
   * per row, or per column when the first row is tenors; a source and
   * additional tenor types may be in any cell.
   */
  // LSEG: AFCellRangeProcessor_29882.processCellRangeSwapPointsCcy
  processCellRangeSwapPointsCcy(range: CellRange, fieldName: string): ForexOverrides {
    const data: Required<ForexOverrides> = { additionalTenorTypes: [], source: "", overrides: [] };
    const rows: CellRange = [];
    let inRows = true;
    let inColumns = false;
    range.forEach((row, i) => {
      rows.push(row);
      [inRows, inColumns] = this.defineRowOrColumnRange(i, row, range, inRows, inColumns);
    });
    if (inRows) this.processSwapPointsInRows(rows, data, fieldName);
    else if (inColumns) this.processSwapPointsInColumns(rows, data);
    data.overrides = data.overrides.filter((o) => Object.keys(o).length !== 0);
    return withoutEmptyValues(data);
  }

  /** Columns when a tenor of the first row heads a column (see checkIfColumnHeader). */
  // LSEG: AFCellRangeProcessor_29882.defineRowOrColumnRange
  private defineRowOrColumnRange(
    i: number,
    row: unknown[],
    range: CellRange,
    inRows: boolean,
    inColumns: boolean,
  ): [boolean, boolean] {
    if (i === 0) {
      row.forEach((cell, j) => {
        if (cell && this.adFinUtils.isTenor(cell) && this.checkIfColumnHeader(row, range, j)) {
          inColumns = true;
          inRows = false;
        }
      });
    }
    return [inRows, inColumns];
  }

  /** A tenor heads a column when a tenor follows it in its row, or a number or a non-tenor lies under it. */
  // LSEG: AFCellRangeProcessor_29882.checkIfColumnHeader
  private checkIfColumnHeader(row: unknown[], range: CellRange, j: number): boolean {
    const under = (cell: unknown) => !!cell && (typeof cell === "number" || !this.adFinUtils.isTenor(cell));
    return (!!row[j + 1] && this.adFinUtils.isTenor(row[j + 1])) || under(range[1]?.[j]) || under(range[2]?.[j]);
  }

  // LSEG: AFCellRangeProcessor_29882.processSwapPointsInRows
  private processSwapPointsInRows(rows: CellRange, data: Required<ForexOverrides>, fieldName: string): void {
    let bidAt = 0;
    let askAt = 0;
    rows.forEach((row) => {
      if (!row) this.adFinUtils.validate(false, fieldName, "argument is not in the correct format");
      const point: SwapPointsRow = { rowTenor: "", rowBid: 0, rowAsk: 0 };
      row.forEach((cell, j) => {
        if (cell === null || cell === undefined) return;
        [bidAt, askAt] = this.getFoundBidAskValues(cell, bidAt, askAt);
        this.checkForSourceOrAdditionalTenorTypes(cell, data);
        this.getRowBidAskValues(cell, point, j, bidAt, askAt);
      });
      data.overrides.push(swapPointsOverride(point));
    });
  }

  // LSEG: AFCellRangeProcessor_29882.processSwapPointsInColumns
  private processSwapPointsInColumns(rows: CellRange, data: Required<ForexOverrides>): void {
    let bidAt = 0;
    let askAt = 0;
    for (let j = 0; j < rows[0].length; j++) {
      const point: SwapPointsRow = { rowTenor: "", rowBid: 0, rowAsk: 0 };
      for (let i = 0; i < rows.length; i++) {
        const cell = rows[i]?.[j];
        if (cell === null || cell === undefined) continue;
        [bidAt, askAt] = this.getFoundBidAskValues(cell, bidAt, askAt);
        this.checkForSourceOrAdditionalTenorTypes(cell, data);
        this.getRowBidAskValues(cell, point, i, bidAt, askAt);
      }
      data.overrides.push(swapPointsOverride(point));
    }
  }

  /** "Bid" / "Ask" label cells place bid and ask: the first one found at 1, the other at 2. */
  // LSEG: AFCellRangeProcessor_29882.getFoundBidAskValues
  private getFoundBidAskValues(cell: unknown, bidAt: number, askAt: number): [number, number] {
    const text = typeof cell === "string" ? cell.toLowerCase() : undefined;
    if (text === "bid") {
      if (askAt === 0) bidAt = 1;
      else if (askAt === 1) bidAt = 2;
    } else if (text === "ask") {
      if (bidAt === 0) askAt = 1;
      else if (bidAt === 1) askAt = 2;
    }
    return [bidAt, askAt];
  }

  /** A tenor cell is the tenor; a number goes to the ask or bid place, else to bid then ask. */
  // LSEG: AFCellRangeProcessor_29882.getRowBidAskValues
  private getRowBidAskValues(cell: unknown, point: SwapPointsRow, at: number, bidAt: number, askAt: number): void {
    if (this.adFinUtils.isTenor(cell)) {
      point.rowTenor = cell;
    } else if (typeof cell === "number" || isNumber(cell)) {
      if (at === askAt) point.rowAsk = cell;
      else if (at === bidAt) point.rowBid = cell;
      else if (point.rowBid) point.rowAsk = cell;
      else point.rowBid = cell;
    }
  }

  // LSEG: AFCellRangeProcessor_29882.checkForSourceOrAdditionalTenorTypes
  private checkForSourceOrAdditionalTenorTypes(cell: unknown, data: Required<ForexOverrides>): void {
    if (this.isSourceValue(cell)) data.source = `${cell}`.trim().toUpperCase();
    else if (this.isAdditionalTenorTypesValue(cell)) data.additionalTenorTypes.push(ADDITIONAL_TENOR_TYPES.get(`${cell}`.toLowerCase()));
  }

  // LSEG: AFCellRangeProcessor_29882.isSourceValue
  private isSourceValue(cell: unknown): boolean {
    return !!cell && isFxSource(`${cell}`.trim().toUpperCase());
  }

  // LSEG: AFCellRangeProcessor_29882.isAdditionalTenorTypesValue
  private isAdditionalTenorTypesValue(cell: unknown): boolean {
    return !!cell && ADDITIONAL_TENOR_TYPES.has(`${cell}`.trim().toLowerCase());
  }
}

/** A row's (or column's) override: its tenor, bid and ask, each left out when empty. */
// LSEG: AFCellRangeProcessor_29882.processSwapPointsInRows / processSwapPointsInColumns (their override object)
function swapPointsOverride(point: SwapPointsRow): Record<string, unknown> {
  return {
    ...(point.rowTenor ? { tenor: point.rowTenor } : {}),
    ...(point.rowBid ? { bid: Number(point.rowBid) } : {}),
    ...(point.rowAsk ? { ask: Number(point.rowAsk) } : {}),
  };
}

/** What a processor read: the arguments, then (after the request is built) the options, positions and fields. */
// LSEG: InputContract (the `e` of BaseAFProcessor.getAdFinRequestInput)
export interface AdFinInputContract {
  args: unknown[];
  options?: Record<string, string | boolean>;
  argIndexMap?: Record<string, number>;
  fields?: string[];
}

/**
 * A date function's request processor. `args` are the Excel arguments in
 * LSEG's order (the function's own, then Options, then Dest). They are kept
 * after two empty slots so their positions are LSEG's (`adfinmain`,
 * `adfintype`, then the function's). A subclass names its arguments with
 * `setArgIndexes` in its constructor and builds the request in `prepareRequestInput`.
 */
// LSEG: BaseAFProcessor_19963.BaseAFProcessor
export abstract class BaseDateProcessor {
  protected argIndexes: Record<string, number> = {};
  fields: string[] = [];
  readonly inputContract: AdFinInputContract;
  protected readonly aFCellRangeProcessor = new AFCellRangeProcessor();
  protected readonly adFinUtils = new AdFinUtils();

  constructor(args: unknown[]) {
    this.inputContract = { args: [null, null, ...args] };
  }

  /** The request item LSEG would send, e.g. `{ calculationDate, calendarCodes, fields }`. */
  abstract prepareRequestInput(): Record<string, unknown>;

  /** Applies the "-" shortcut, builds the request, then records options, positions and fields. */
  // LSEG: BaseAFProcessor_19963.getAdFinRequestInput
  getAdFinRequestInput(): Record<string, unknown> {
    this.setInputContract();
    const request = this.prepareRequestInput();
    this.updateInputContract();
    return request;
  }

  /**
   * An argument that is only "-" ends the optional arguments: the argument
   * after it is taken as Options, and the ones from the "-" up to Options are blanked.
   */
  // LSEG: BaseAFProcessor_19963.setInputContract
  protected setInputContract(): void {
    const args = this.inputContract.args;
    const options = this.argIndexes[AF_ARG.options];
    if (args.length - 1 > 0 && options && options > 2) {
      for (let i = 2; i < args.length; i++) {
        const arg = args[i];
        if (typeof arg === "string" && AdFinUtils.containsOnlySymbol(arg, "-")) {
          args[options] = args[i + 1];
          for (let j = i; j < options; j++) args[j] = null;
          break;
        }
      }
    }
  }

  // LSEG: BaseAFProcessor_19963.updateInputContract
  protected updateInputContract(): void {
    this.setInputOptions();
    this.inputContract.argIndexMap = this.argIndexes; // LSEG: BaseAFProcessor_19963.setInputArgIndexes
    this.inputContract.fields = this.fields; // LSEG: BaseAFProcessor_19963.setInputFields
  }

  /**
   * Options text to an object, lower-cased: "CH:Fd RH:no transpose" ->
   * { ch: "fd", rh: "no", norh: true, transpose: true }. Left unset when there is none.
   * [A] A non-text Options value is read as its text (LSEG would fail on it).
   */
  // LSEG: BaseAFProcessor_19963.setInputOptions
  protected setInputOptions(): void {
    const parsed: Record<string, string | boolean> = {};
    let any = false;
    `${this.getArg(AF_ARG.options) ?? ""}`
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .forEach((word) => {
        let key = word;
        let value: string | boolean = true;
        if (word.includes(":")) [key, value] = word.split(":");
        if (key && value) {
          if (value === "no") parsed[`no${key}`] = true;
          parsed[key] = value;
          any = true;
        }
      });
    if (any) this.inputContract.options = { ...this.inputContract.options, ...parsed };
  }

  /** Positions: LSEG's two leading slots, the function's arguments, then Options and Dest. */
  // LSEG: BaseAFProcessor_19963.setArgIndexes
  protected setArgIndexes(names: AFArgName[]): void {
    this.argIndexes[AF_ARG.adFinMain] = 0;
    this.argIndexes[AF_ARG.adFinType] = 1;
    let next = names.length + 2;
    names.forEach((name, i) => {
      this.argIndexes[name] = i + 2;
    });
    if (!this.argIndexes[AF_ARG.options]) this.argIndexes[AF_ARG.options] = next++;
    if (!this.argIndexes[AF_ARG.destinationCell]) this.argIndexes[AF_ARG.destinationCell] = next;
  }

  // LSEG: BaseAFProcessor_19963.setArgIndex
  protected setArgIndex(name: AFArgName, sameAs: AFArgName): void {
    this.argIndexes[name] = this.argIndexes[sameAs];
  }

  // LSEG: BaseAFProcessor_19963.getArg
  protected getArg(name: AFArgName): unknown {
    return this.inputContract.args[this.argIndexes[name]];
  }

  /**
   * A date (an Excel serial, a date text, "Label:date", or a range of one
   * cell or a label cell then the date) as "YYYY-MM-DD"; undefined when blank
   * and not mandatory.
   */
  // LSEG: BaseAFProcessor_19963.getAdfinDate
  protected getAdfinDate(value: unknown, argName: ArgName, isMandatory: boolean): string | null | undefined {
    const [label, name] = labelAndName(argName);
    if (isMandatory) this.validateNotEmpty(value, name);
    if (!value) return undefined;
    if (Array.isArray(value)) {
      const tooBig = value[0].length > 2 || value.length > 2;
      this.adFinUtils.validate(!tooBig, name, "not in the right format");
      return this.aFCellRangeProcessor.processCellRangeAdfinDate(value, argName);
    }
    if (typeof value === "number") return this.adFinUtils.getFormattedDate({ date: Math.floor(value) });
    if (typeof value !== "string") this.adFinUtils.notValid(name, "not in the right format");
    const [labelled, , text] = this.adFinUtils.getLabelAndValue(`${value}`, label, name);
    return this.adFinUtils.getFormattedDate({ date: labelled ? text : value, fieldName: name });
  }

  /** A period: a tenor ("3M", "1Y6M", "ON", ...) or a date; undefined when blank and not mandatory. */
  // LSEG: BaseAFProcessor_19963.getPeriod
  protected getPeriod(value: unknown, argName: ArgName, isMandatory: boolean): string | undefined {
    const [label, name] = labelAndName(argName);
    if (isMandatory) this.validateNotEmpty(value, name);
    if (!value) return undefined;
    if (Array.isArray(value)) return this.aFCellRangeProcessor.processCellRangePeriod(value, argName);
    if (typeof value === "number") return this.getTenor(value)[1];
    this.adFinUtils.validate(typeof value === "string", name, "not in the right format");
    const [labelled, , text] = this.adFinUtils.getLabelAndValue(`${value}`, label, name);
    const [valid, period] = this.getTenor(labelled ? text : value);
    this.adFinUtils.validate(valid, name, `bad syntax or period '${period}' is not in right format`);
    return period;
  }

  /** Calendar codes from "EMU;FRA", "Calendar Codes:EMU" or a row/column of cells. */
  // LSEG: BaseAFProcessor_19963.getCalendarCodes
  protected getCalendarCodes(value: unknown, isMandatory?: boolean): string[] | undefined {
    if (Array.isArray(value) && this.aFCellRangeProcessor.isBlankRange(value)) value = undefined; // [A] all-blank range = empty
    if (isMandatory) this.validateNotEmpty(value, "Calendar Codes");
    if (!value) return undefined;
    if (Array.isArray(value)) return this.aFCellRangeProcessor.processCellRangeCalendarCurrencyCodes(value, "calendar");
    if (typeof value !== "string") this.adFinUtils.notValid("Calendar Codes", "invalid value");
    const [labelled, , text] = this.adFinUtils.getLabelAndValue(`${value}`, "Calendar Codes", "Calendar Codes");
    return this.adFinUtils.processCalendarCodes(labelled ? text : (value as string));
  }

  /** Currency codes from "EUR;USD", "Currency Codes:EUR" or a row/column of cells. */
  // LSEG: BaseAFProcessor_19963.getCurrencyCodes
  protected getCurrencyCodes(value: unknown, isMandatory?: boolean): string[] | undefined {
    if (Array.isArray(value) && this.aFCellRangeProcessor.isBlankRange(value)) value = undefined; // [A] all-blank range = empty
    if (isMandatory) this.validateNotEmpty(value, "Currency Codes");
    if (!value) return undefined;
    if (Array.isArray(value)) return this.aFCellRangeProcessor.processCellRangeCalendarCurrencyCodes(value, "currency");
    if (typeof value !== "string") this.adFinUtils.notValid("Currency Codes", "invalid value");
    const [labelled, , text] = this.adFinUtils.getLabelAndValue(value as string, "Currency Codes", "Currency Codes");
    return this.adFinUtils.processCurrencyCodes(labelled ? text : (value as string));
  }

  /** [is a date or a tenor, the ISO date or the tenor text]. */
  // LSEG: BaseAFProcessor_19963.getTenor
  protected getTenor(value: unknown): [boolean, string] {
    try {
      return [true, this.getAdfinDate(value, { tenor: "Tenor" }, false) as string];
    } catch {
      const text = `${value}`;
      return [this.adFinUtils.isTenor(text), text];
    }
  }

  /**
   * Tenors (or dates) from "1M;3M", "Tenors:1M,3M", "{1M|3M}", an Excel
   * serial or a row/column of cells; every bad one is named. Undefined when
   * blank and not mandatory.
   */
  // LSEG: BaseAFProcessor_19963.getTenors
  protected getTenors(value: unknown, isMandatory: boolean): string[] | undefined {
    if (isMandatory) this.validateNotEmpty(value, "Tenor");
    if (!value) return undefined;
    if (Array.isArray(value)) return this.aFCellRangeProcessor.processCellRangeTenors(value);
    if (typeof value === "number") return [this.getTenor(value)[1]];
    let text = `${value}`.trim();
    const [labelled, , unlabelled] = this.adFinUtils.getLabelAndValue(text, "Tenors", "Tenors");
    if (labelled) text = unlabelled;
    const closes = text.endsWith("}");
    if (text.startsWith("{")) {
      this.adFinUtils.validate(closes, "Tenors", "bad syntax, not matching { and }");
      text = text.slice(1, -1);
    } else {
      this.adFinUtils.validate(!closes, "Tenors", "bad syntax, not matching { and }");
    }
    let valid = true;
    const problems: string[] = [];
    const tenors = text.split(LIST_SEPARATORS).map((part) => {
      const [ok, tenor] = this.getTenor(part.trim());
      if (!ok) {
        valid = false;
        problems.push(`'${tenor}' is not valid tenor`);
      }
      return tenor;
    });
    this.adFinUtils.validate(valid, "Tenors", problems.join(", "));
    return tenors;
  }

  // LSEG: BaseAFProcessor_19963.validateNotEmpty
  protected validateNotEmpty(value: unknown, fieldName: string): void {
    this.adFinUtils.validateNotEmpty(value, fieldName);
  }

  /** Names from "a;b", "Label:a;b" or "{a;b}", mapped through `allowed` (keys are fieldNameToLabel forms). */
  // LSEG: BaseAFProcessor_19963.extractAdfinArgument
  protected extractAdfinArgument(value: unknown, allowed: Record<string, string>, fieldName: string): string[] {
    const label = this.adFinUtils.fieldNameToLabel(fieldName);
    const out: string[] = [];
    const [labelled, , text] = this.adFinUtils.getLabelAndValue(`${value}`, label, fieldName);
    let list = labelled ? text : `${value}`.trim();
    const closes = list.endsWith("}");
    if (list.startsWith("{")) {
      this.adFinUtils.validate(closes, fieldName, "bad syntax, not matching { and }");
      list = list.slice(1, -1).trim();
    } else {
      this.adFinUtils.validate(!closes, fieldName, "bad syntax, not matching { and }");
    }
    for (const name of list.split(LIST_SEPARATORS).map((n) => this.adFinUtils.fieldNameToLabel(n))) {
      const mapped = allowed[name];
      this.adFinUtils.validate(mapped !== undefined, fieldName, `value '${name}' is not valid`);
      out.push(mapped);
    }
    this.adFinUtils.validate(out.length > 0, fieldName, "no values in list");
    return out;
  }

  /**
   * Sets `fields` from the user's Fields argument, each name mapped to its
   * spelling in `allowed`; `keyColumnField` is put first when missing.
   */
  // LSEG: BaseAFProcessor_19963.setOutputFields
  protected setOutputFields(value: unknown, allowed: readonly string[], fieldName: string, keyColumnField?: string): void {
    this.fields = [];
    if (!value) return;
    const map = this.adFinUtils.listToObject(allowed);
    this.fields = Array.isArray(value)
      ? this.aFCellRangeProcessor.processCellRangeArgument(value, map, fieldName)
      : this.extractAdfinArgument(value, map, fieldName);
    if (keyColumnField) this.getFieldsWithKeyColumnField(keyColumnField);
  }

  // LSEG: BaseAFProcessor_19963.getFieldsWithKeyColumnField
  protected getFieldsWithKeyColumnField(keyColumnField: string): void {
    if (!this.fields.includes(keyColumnField)) this.fields.unshift(keyColumnField);
  }

  // LSEG: BaseAFProcessor_19963.getDayCountBasis
  protected getDayCountBasis(value: unknown): string | undefined {
    const fieldName = "Day Count Basis";
    if (!value) return undefined;
    return this.oneOf(value, DAY_COUNT_BASES, fieldName);
  }

  // LSEG: BaseAFProcessor_19963.getDateMovingConvention
  protected getDateMovingConvention(value: unknown): string {
    const fieldName = "Date Moving Convention";
    if (!value) return "";
    return this.oneOf(value, DATE_MOVING_CONVENTIONS, fieldName);
  }

  /** The one name `value` gives from `allowed`, from a text or a range (the shared tail of LSEG's getters). */
  private oneOf(value: unknown, allowed: readonly string[], fieldName: string): string {
    const map = this.adFinUtils.listToObject(allowed);
    const names = Array.isArray(value)
      ? this.aFCellRangeProcessor.processCellRangeArgument(value, map, fieldName)
      : this.extractAdfinArgument(`${value}`, map, fieldName);
    this.adFinUtils.validate(names.length === 1, fieldName, "not in a valid format");
    return names[0];
  }
}

// LSEG: BaseAFProcessor_19963.getDayCountBasis (its list)
const DAY_COUNT_BASES = ["Dcb_30_360", "Dcb_30_Actual", "Dcb_Actual_360", "Dcb_Actual_365", "Dcb_Actual_Actual"];
// LSEG: BaseAFProcessor_19963.getDateMovingConvention (its list)
const DATE_MOVING_CONVENTIONS = [
  "PreviousBusinessDay",
  "NextBusinessDay",
  "ModifiedFollowing",
  "NoMoving",
  "EveryThirdWednesday",
  "BBSWModifiedFollowing",
];
